<template>
  <main class="notes-page">
    <nav class="top-bar" aria-label="ページ移動">
      <router-link to="/" class="back-link">
        <IconSvg name="house-solid-full" size="sm" />
        <span>トップに戻る</span>
      </router-link>
    </nav>

    <section class="hub-shell" aria-labelledby="notes-title">
      <div class="intro-panel">
        <p class="eyebrow">旅のメモと準備</p>
        <h1 id="notes-title">しおりごとの準備メモを開く</h1>
        <p class="lead">
          旅行メモはTo Do、持ち物、行きたいところに整理できます。準備したいしおりを選んで、必要なタブへ進んでください。
        </p>
      </div>

      <div v-if="shioriStore.isLoading" class="state-panel" role="status">
        <IconSvg name="circle-info-solid-full" size="md" />
        <span>しおりを読み込んでいます。</span>
      </div>

      <div v-else-if="availableShiori.length" class="shiori-list" aria-label="しおり一覧">
        <article
          v-for="item in availableShiori"
          :key="item.SHIORI_NOV"
          class="shiori-row"
        >
          <div class="shiori-summary">
            <p class="trip-label">{{ formatTripDate(item) }}</p>
            <h2>{{ item.SUBJECT_NMV || "名称未設定のしおり" }}</h2>
            <p>{{ formatDestinations(item) }}</p>
          </div>

          <div class="prep-actions" aria-label="準備タブを開く">
            <router-link :to="prepLink(item, 'todo')" class="prep-link">
              <IconSvg name="check-solid-full" size="sm" />
              <span>To Do</span>
            </router-link>
            <router-link :to="prepLink(item, 'belongings')" class="prep-link">
              <IconSvg name="bag-shopping-solid-full" size="sm" />
              <span>持ち物</span>
            </router-link>
            <router-link :to="prepLink(item, 'wishlist')" class="prep-link">
              <IconSvg name="map-solid-full" size="sm" />
              <span>行きたいところ</span>
            </router-link>
          </div>
        </article>
      </div>

      <section v-else class="empty-panel" aria-labelledby="empty-title">
        <IconSvg name="question-solid-full" size="xl" />
        <div>
          <h2 id="empty-title">準備するしおりがまだありません</h2>
          <p>まずは旅行計画を作るか、マイページからしおりを作成してください。</p>
        </div>
        <div class="empty-actions">
          <router-link to="/plan" class="primary-link">旅行計画を始める</router-link>
          <router-link to="/mypage" class="secondary-link">しおり一覧へ</router-link>
        </div>
      </section>
    </section>
  </main>
</template>

<script>
import { computed, onMounted } from "vue";
import IconSvg from "@/components/IconSvg.vue";
import { useShioriStore } from "@/store/shiori-store.js";

export default {
  name: "NotesView",
  components: {
    IconSvg,
  },
  setup() {
    const shioriStore = useShioriStore();

    const availableShiori = computed(() => {
      const own = Array.isArray(shioriStore.shiori) ? shioriStore.shiori : [];
      const shared = Array.isArray(shioriStore.sharedShiori)
        ? shioriStore.sharedShiori
        : [];
      const seen = new Set();

      return [...own, ...shared].filter((item) => {
        const id = item?.SHIORI_NOV;
        if (!id || seen.has(id)) {
          return false;
        }
        seen.add(id);
        return true;
      });
    });

    const formatTripDate = (item) => {
      const from = item?.TRIP_FROM || item?.START_YMD;
      const to = item?.TRIP_TO || item?.END_YMD;
      if (from && to && from !== to) {
        return `${from} - ${to}`;
      }
      return from || "日程未設定";
    };

    const formatDestinations = (item) => {
      const destinations = item?.DESTINATIONS;
      if (!destinations) {
        return item?.DESTINATION_NMV || "行先未設定";
      }

      if (Array.isArray(destinations)) {
        return destinations.join("、") || "行先未設定";
      }

      try {
        const parsed = JSON.parse(destinations);
        return Array.isArray(parsed) && parsed.length
          ? parsed.join("、")
          : "行先未設定";
      } catch (error) {
        return destinations;
      }
    };

    const prepLink = (item, tab) => ({
      path: `/shiori/${item.SHIORI_NOV}`,
      query: { tab },
    });

    onMounted(async () => {
      await Promise.all([
        shioriStore.fetchShiori({ force: true }),
        shioriStore.fetchSharedWithMe({ force: true }),
      ]);
    });

    return {
      availableShiori,
      formatDestinations,
      formatTripDate,
      prepLink,
      shioriStore,
    };
  },
};
</script>

<style scoped>
.notes-page {
  min-height: 100vh;
  background: #f5f7fb;
  color: #18202f;
}

.top-bar {
  border-bottom: 1px solid #dde3ee;
  background: #ffffff;
  padding: 16px clamp(16px, 4vw, 40px);
}

.back-link {
  align-items: center;
  background: #eef2f7;
  border-radius: 8px;
  color: #334155;
  display: inline-flex;
  font-weight: 700;
  gap: 10px;
  padding: 10px 14px;
  text-decoration: none;
}

.back-link:hover,
.back-link:focus-visible {
  background: #e2e8f0;
}

.hub-shell {
  display: grid;
  gap: 18px;
  margin: 0 auto;
  max-width: 1040px;
  padding: clamp(24px, 5vw, 48px) clamp(16px, 4vw, 32px);
}

.intro-panel,
.state-panel,
.shiori-row,
.empty-panel {
  background: #ffffff;
  border: 1px solid #dce4ef;
  border-radius: 8px;
}

.intro-panel {
  padding: clamp(20px, 4vw, 32px);
}

.eyebrow {
  color: #2f6f73;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0 0 8px;
}

h1,
h2,
p {
  margin: 0;
}

h1 {
  font-size: clamp(1.65rem, 4vw, 2.25rem);
  line-height: 1.25;
}

.lead {
  color: #536173;
  line-height: 1.75;
  margin-top: 14px;
  max-width: 760px;
}

.state-panel {
  align-items: center;
  color: #536173;
  display: flex;
  gap: 10px;
  padding: 18px;
}

.shiori-list {
  display: grid;
  gap: 12px;
}

.shiori-row {
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr) auto;
  padding: 18px;
}

.shiori-summary {
  display: grid;
  gap: 6px;
  min-width: 0;
}

.trip-label {
  color: #2f6f73;
  font-size: 0.82rem;
  font-weight: 800;
}

.shiori-summary h2 {
  font-size: 1.12rem;
  line-height: 1.35;
}

.shiori-summary p:last-child {
  color: #617084;
  line-height: 1.5;
}

.prep-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.prep-link,
.primary-link,
.secondary-link {
  align-items: center;
  border-radius: 8px;
  display: inline-flex;
  font-weight: 800;
  gap: 8px;
  min-height: 40px;
  padding: 10px 13px;
  text-decoration: none;
}

.prep-link {
  background: #eef7f4;
  border: 1px solid #c8ded9;
  color: #1f5f63;
}

.prep-link:hover,
.prep-link:focus-visible {
  border-color: #2f6f73;
}

.empty-panel {
  align-items: center;
  display: grid;
  gap: 18px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  padding: 22px;
}

.empty-panel h2 {
  font-size: 1.15rem;
  margin-bottom: 6px;
}

.empty-panel p {
  color: #617084;
  line-height: 1.6;
}

.empty-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.primary-link {
  background: #24364b;
  color: #ffffff;
}

.secondary-link {
  background: #eef2f7;
  color: #334155;
}

@media (max-width: 820px) {
  .shiori-row,
  .empty-panel {
    grid-template-columns: 1fr;
  }

  .prep-actions,
  .empty-actions {
    justify-content: flex-start;
  }
}
</style>
